import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { SEO } from "@/lib/seo";
import { GraduationCap, Award, Heart, Building2, Video } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function AboutPage() {
    const { t } = useTranslation();
    return (
        <>
            <SEO title={t("about.seoTitle")} description={t("about.seoDescription")} />
            <div className="container-page pt-8">
                <Breadcrumbs items={[{ label: t("about.crumb") }]} />
            </div>

            <section className="container-page pt-6 pb-4">
                <div className="grid gap-10 lg:grid-cols-12 items-start">
                    <div className="lg:col-span-5">
                        <figure
                            className="relative overflow-hidden rounded-[28px] border border-[color:var(--border-default)] bg-white"
                            style={{ boxShadow: "0 30px 80px rgba(15,39,71,0.10)" }}
                            data-testid="about-doctor-portrait"
                        >
                            <div className="relative aspect-[4/5] w-full bg-[color:var(--bg-subtle)]">
                                <img
                                    src="/doctor/dr-kichian-portrait.png"
                                    alt={t("about.portraitAlt")}
                                    className="absolute inset-0 h-full w-full object-cover object-center"
                                    loading="eager"
                                    draggable={false}
                                />
                                <div
                                    aria-hidden
                                    className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
                                    style={{
                                        background:
                                            "linear-gradient(180deg, rgba(15,39,71,0) 0%, rgba(15,39,71,0.55) 100%)",
                                    }}
                                />
                            </div>
                            <figcaption
                                className="absolute inset-x-0 bottom-0 px-5 py-3 bg-[color:var(--brand-navy)] text-white flex items-center justify-center gap-2"
                                data-testid="about-doctor-nameplate"
                            >
                                <span className="inline-block h-2 w-2 rounded-full bg-[color:var(--brand-teal-2)]" aria-hidden />
                                <p className="font-display text-base sm:text-lg font-semibold tracking-wide">
                                    Dr K. Kichian&ensp;
                                    <span className="text-[color:var(--brand-teal-soft)] font-medium">
                                        {t("about.nameSuffix")}
                                    </span>
                                </p>
                            </figcaption>
                        </figure>
                    </div>
                    <div className="lg:col-span-7">
                        <span className="chip-teal">{t("about.chip")}</span>
                        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                            {t("about.title")}
                        </h1>
                        <p className="mt-5 text-[color:var(--text-muted)] leading-relaxed text-lg">
                            <Trans i18nKey="about.p1" components={{ 1: <strong className="text-[color:var(--brand-navy)]" /> }} />
                        </p>
                        <p className="mt-4 text-[color:var(--text-muted)] leading-relaxed">
                            <Trans i18nKey="about.p2" components={{ 1: <strong className="text-[color:var(--brand-navy)]" /> }} />
                        </p>
                        <p className="mt-4 text-[color:var(--text-muted)] leading-relaxed">
                            <Trans i18nKey="about.p3" components={{ 1: <strong className="text-[color:var(--brand-teal-2)]" /> }} />
                        </p>
                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            <Button asChild size="lg" data-testid="about-virtual-cta">
                                <Link to="/virtual-consultations">{t("cta.aboutVirtualCta")}</Link>
                            </Button>
                            <Button asChild variant="outline" size="lg">
                                <Link to="/referrals">{t("nav.referringPhysicians")}</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container-page section-y">
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                    <InfoCard icon={GraduationCap} title={t("about.card1Title")} body={t("about.card1Body")} />
                    <InfoCard icon={Award} title={t("about.card2Title")} body={t("about.card2Body")} />
                    <InfoCard icon={Building2} title={t("about.card3Title")} body={t("about.card3Body")} />
                    <InfoCard icon={Award} title={t("about.card4Title")} body={t("about.card4Body")} />
                    <InfoCard icon={Heart} title={t("about.card5Title")} body={t("about.card5Body")} />
                    <InfoCard icon={Video} title={t("about.card6Title")} body={t("about.card6Body")} />
                </div>
            </section>
        </>
    );
}

function InfoCard({ icon: Icon, title, body }) {
    return (
        <div className="card-soft p-6">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)]">
                <Icon className="h-5 w-5" />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold text-[color:var(--brand-navy)]">{title}</h3>
            <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">{body}</p>
        </div>
    );
}
